import React, { ChangeEvent } from 'react';

import { TextField } from './textfield.component';
import { EmailProps } from '../../interfaces';
import { checkRegex } from '../../validations';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { TooltipContainer } from '../tooltip-container';

export function EmailInput({ name, ...props }: EmailProps): JSX.Element {
  const contextRules = useGetFieldRules<EmailProps>(name);
  return <EmailInputComponent {...{ ...contextRules, ...props }} name={name} />;
}

export function EmailInputComponent(props: EmailProps): JSX.Element {
  const getError = (value: unknown): string[] | null => {
    const emailRegex = '[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+';
    const singleEmail = `^${emailRegex}$`;
    const multipleEmail = `^${emailRegex}(([;])${emailRegex}){0,4}?$`;

    if (!checkRegex(value, props.isMultiple ? multipleEmail : singleEmail)) {
      return [props.customFieldProps?.errorMessages?.invalidEmail ?? 'Invalid email address'];
    }

    return null;
  };

  const { error, validateInput } = useValidateInput(props, getError);
  const { setValueByName } = useFormContext();
  const allProps = { ...props, tooltip: '' };
  const setValue = (value: string): void => {
    setValueByName(props.name, value);
    if (props.onChange) {
      props.onChange(value);
    }
    validateInput(value);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
  };

  React.useEffect(() => {
    if (props.defaultValue && props.defaultValue !== '') {
      setValue(props.defaultValue);
    }
    if (props.defaultValue != null) validateInput(props.defaultValue);
  }, []);

  return (
    <TooltipContainer tooltip={props.tooltip}>
      <TextField
        {...allProps}
        muiProps={{
          name: props.name,
          onChange: handleChange,
          helperText: error ? error : props.helperText,
          error: error != null,
          type: 'email'
        }}
      />
    </TooltipContainer>
  );
}
